import type { FormLocale } from '@jlg-website/shared';

/**
 * Copy of both call-back flows, mirroring how the form package keeps its own labels per locale.
 * Owned here rather than by either flow, so neither has to reach into the other's dictionary.
 */
const copy = {
  en: {
    stepOf: 'Step {step} of {total}',
    callNowQuestion: 'Would you like us to call you now?',
    callNowPermission: 'Would you give us permission to call you now?',
    yes: 'Yes, call me now',
    no: "No, I'd prefer to schedule a callback.",
    timeZone: 'Your time zone',
    whenToCall: 'When can we call you?',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    continue: 'Continue',
    backToQuestion: 'Back',
    backToCalendar: 'Back to the calendar',
    backToTimes: 'Back to the available times',
    availableTimes: 'Available times',
    noTimesLeft: 'No times left for this part of the day. Try another one.',
    confirmTitle: 'Confirm your call back',
    change: 'Change',
    weWillCall: "We'll call",
    notesLabel: 'Anything we should know?',
    optional: '(optional)',
    notesPlaceholder:
      'The more you can tell us before the call, the better we can help — best times, questions, anything on your mind.',
    confirmAction: 'Confirm my call back',
    callNowSummary: 'Now',
    deadline: 'Confirm within {time} — after that you will need to pick a new time.',
    failed: 'We could not schedule your call back. Please try again.',
    successTitle: 'Your call back is scheduled',
    successHint: 'Keep an eye on your phone around that time.',
    summary: '{day} at {time} ({zone})',
  },
  es: {
    stepOf: 'Paso {step} de {total}',
    callNowQuestion: '¿Desea que le llamemos ahora?',
    callNowPermission: '¿Nos da permiso para llamarle ahora?',
    yes: 'Sí, llámame ahora.',
    no: 'No, prefiero programar una llamada de vuelta.',
    timeZone: 'Su zona horaria',
    whenToCall: '¿Cuándo podemos llamarle?',
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    continue: 'Continuar',
    backToQuestion: 'Volver',
    backToCalendar: 'Volver al calendario',
    backToTimes: 'Volver a los horarios disponibles',
    availableTimes: 'Horarios disponibles',
    noTimesLeft: 'No quedan horarios en esta parte del día. Pruebe con otra.',
    confirmTitle: 'Confirme su llamada',
    change: 'Cambiar',
    weWillCall: 'Llamaremos al',
    notesLabel: '¿Algo que debamos saber?',
    optional: '(opcional)',
    notesPlaceholder:
      'Cuanto más pueda contarnos antes de la llamada, mejor podremos ayudarle: mejores horarios, preguntas, lo que tenga en mente.',
    confirmAction: 'Confirmar mi llamada',
    callNowSummary: 'Ahora',
    deadline: 'Confirme en {time} — después deberá elegir un nuevo horario.',
    failed: 'No pudimos agendar su llamada. Por favor, inténtelo de nuevo.',
    successTitle: 'Su llamada está agendada',
    successHint: 'Esté atento a su teléfono alrededor de ese horario.',
    summary: '{day} a las {time} ({zone})',
  },
} satisfies Record<FormLocale, Record<string, string>>;

/** Every label the flows can ask for — a key that does not exist stops compiling. */
export type CopyKey = keyof (typeof copy)['en'];

/** Picks this locale's copy, falling back to English exactly like the form renderer does. */
export const textFor = (locale: FormLocale, key: CopyKey) => copy[locale]?.[key] ?? copy.en[key];
